import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (args: {
        url: string;
        parentElement: HTMLElement;
        resize?: boolean;
      }) => void;
    };
  }
}

interface CalendlyModalProps {
  isOpen: boolean;
  calendlyUrl: string;
  title?: string;
  onClose: () => void;
}

function withCalendlyParams(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}hide_gdpr_banner=1`;
}

export default function CalendlyModal({ isOpen, calendlyUrl, title, onClose }: CalendlyModalProps) {
  const widgetContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);

    const initWidget = () => {
      if (!widgetContainerRef.current || !window.Calendly?.initInlineWidget) return;
      widgetContainerRef.current.innerHTML = '';
      window.Calendly.initInlineWidget({
        url: withCalendlyParams(calendlyUrl),
        parentElement: widgetContainerRef.current,
        resize: true,
      });
    };

    const existingScript = document.querySelector(
      'script[src="https://assets.calendly.com/assets/external/widget.js"]'
    ) as HTMLScriptElement | null;

    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.onload = initWidget;
      document.head.appendChild(script);
    } else {
      initWidget();
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, calendlyUrl, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm p-4 md:p-8 flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title ?? 'Reserva tu cita'}
    >
      <div
        className="w-full max-w-5xl bg-white rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 md:px-6 py-4 border-b border-stone-200">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#8B9D83] font-semibold">Calendly</p>
            <h3 className="text-[#1A1A1A] font-semibold text-base md:text-lg">
              {title ?? 'Reserva tu cita'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-full border border-stone-200 hover:bg-stone-100 transition-colors"
            aria-label="Cerrar modal"
          >
            <i className="ri-close-line text-xl text-[#1A1A1A]"></i>
          </button>
        </div>

        <div className="h-[75vh] min-h-[560px]">
          <div ref={widgetContainerRef} className="w-full h-full" />
        </div>
      </div>
    </div>
  );
}
